import React, { useState, useEffect, useMemo } from 'react';
import { User, Document } from '../types';
import { Folder, FileText, ChevronRight, Grid, List as ListIcon, Download, Eye, Search, ArrowLeft, Home, Loader2, CheckSquare, Square, X } from 'lucide-react';
import { api } from '../services/api';
import { formatDate } from '../utils/formatters';
import JSZip from 'jszip';

interface FileBrowserViewProps {
    currentUser: User;
    agencyClients: User[]; // All clients available to the agency/member
}

type ViewMode = 'grid' | 'list';

interface FileSystemItem {
    id: string; // Unique ID for selection
    name: string;
    type: 'folder' | 'file';
    metadata?: any; // For files: Document object; For folders: count, etc.
    icon?: any;
}

export function FileBrowserView({ currentUser, agencyClients }: FileBrowserViewProps) {
    const [viewMode, setViewMode] = useState<ViewMode>('grid');
    const [currentPath, setCurrentPath] = useState<FileSystemItem[]>([]); // Breadcrumb trail
    const [isLoading, setIsLoading] = useState(false);
    const [documents, setDocuments] = useState<Document[]>([]); // Current level documents
    const [searchQuery, setSearchQuery] = useState('');

    // Bulk Selection State
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
    const [isDownloading, setIsDownloading] = useState(false);

    // --- Derived State & Logic ---

    // 1. Load Documents logic (same as before)
    useEffect(() => {
        const loadDocs = async () => {
            setIsLoading(true);
            try {
                let agencyId = '';
                let clientId = '';

                if (currentUser.role === 'agency') {
                    agencyId = currentUser.id;
                    if (currentPath.length >= 1 && currentPath[0].type === 'folder') {
                        clientId = currentPath[0].id;
                    }
                } else if (currentUser.role === 'agency_member') {
                    agencyId = currentUser.agencyId || '';
                    if (currentPath.length >= 1 && currentPath[0].type === 'folder') {
                        clientId = currentPath[0].id;
                    }
                } else {
                    agencyId = currentUser.agencyId || '';
                    clientId = currentUser.id;
                }

                if (!agencyId || !clientId) {
                    setIsLoading(false);
                    return;
                }

                const docs = await api.getDocuments(agencyId, clientId);
                setDocuments(docs);
            } catch (error) {
                console.error("Failed to load documents", error);
            } finally {
                setIsLoading(false);
            }
        };

        loadDocs();
    }, [currentPath, currentUser]);

    // Clear selection when navigating
    useEffect(() => {
        setSelectedItems(new Set());
    }, [currentPath]);

    // 3. Compute Items to Display based on Path
    const items: FileSystemItem[] = useMemo(() => {
        // A. Root Level for Agency/Member: Show Clients (No Bulk Download for Clients list for now)
        if ((currentUser.role === 'agency' || currentUser.role === 'agency_member') && currentPath.length === 0) {
            let displayedClients = agencyClients;
            if (searchQuery) {
                displayedClients = agencyClients.filter(c =>
                    (c.profile?.companyName || c.config.companyName || c.email).toLowerCase().includes(searchQuery.toLowerCase())
                );
            }
            return displayedClients.map(client => ({
                id: client.id,
                name: client.profile?.companyName || client.config.companyName || client.email,
                type: 'folder',
                metadata: { client }
            }));
        }

        // B. Document Hierarchy
        let filteredDocs = documents;
        if (searchQuery) {
            filteredDocs = documents.filter(d => d.typeName.toLowerCase().includes(searchQuery.toLowerCase()));
        }

        // Helper to get Year safely
        const getDocYear = (d: Document) => {
            const val = d.ai_analysis?.doc_date || d.createdAt;
            if (!val) return 'Unknown';

            let date: Date;
            if (typeof val === 'object' && 'seconds' in val) {
                date = new Date((val as any).seconds * 1000);
            } else {
                date = new Date(val as string);
            }

            if (isNaN(date.getTime())) return 'Unknown';
            return date.getFullYear().toString();
        };

        const isAgencyUser = currentUser.role === 'agency' || currentUser.role === 'agency_member';
        const relativePath = isAgencyUser ? currentPath.slice(1) : currentPath;

        // Level 0: Years
        if (relativePath.length === 0) {
            const years = new Set(filteredDocs.map(d => getDocYear(d)));

            return Array.from(years).sort().reverse().map(year => ({
                id: `year-${year}`,
                name: year,
                type: 'folder',
                metadata: { count: filteredDocs.filter(d => getDocYear(d) === year).length }
            }));
        }

        // Level 1: Document Types
        if (relativePath.length === 1) {
            const currentYear = relativePath[0].name;
            const periodDocs = filteredDocs.filter(d => getDocYear(d) === currentYear);
            const types = new Set(periodDocs.map(d => d.typeName));

            return Array.from(types).sort().map(type => ({
                id: `type-${type}`,
                name: type,
                type: 'folder',
                metadata: { count: periodDocs.filter(d => d.typeName === type).length }
            }));
        }

        // Level 2: Files
        if (relativePath.length === 2) {
            const currentYear = relativePath[0].name;
            const currentType = relativePath[1].name;

            return filteredDocs
                .filter(d => getDocYear(d) === currentYear && d.typeName === currentType)
                .map(d => ({
                    id: d.id,
                    name: d.title || d.typeName,
                    type: 'file',
                    metadata: d
                }));
        }

        return [];

    }, [currentUser, agencyClients, currentPath, documents, searchQuery]);

    // --- Handlers ---

    const handleItemClick = (item: FileSystemItem, e?: React.MouseEvent) => {
        // If clicking checkbox area or in selection mode, toggle selection
        // However, we want strict separation: Clicking icon/name navigates, clicking checkbox selects.
        // But for better UX, if selection mode is active (items > 0), clicking usually selects.

        // Simpler: Click navigates, Click on Checkbox selects.
        if (item.type === 'folder') {
            setCurrentPath([...currentPath, item]);
            setSearchQuery('');
        } else {
            window.open(item.metadata.fileUrl, '_blank');
        }
    };

    const handleToggleSelect = (e: React.MouseEvent, itemId: string) => {
        e.stopPropagation();
        const newSet = new Set(selectedItems);
        if (newSet.has(itemId)) {
            newSet.delete(itemId);
        } else {
            newSet.add(itemId);
        }
        setSelectedItems(newSet);
    };

    const handleSelectAll = () => {
        if (selectedItems.size === items.length) {
            setSelectedItems(new Set());
        } else {
            setSelectedItems(new Set(items.map(i => i.id)));
        }
    };

    const handleDownloadSelected = async () => {
        if (selectedItems.size === 0) return;
        setIsDownloading(true);

        try {
            const zip = new JSZip();
            const folderName = "documents_export";
            const root = zip.folder(folderName);

            // Helper to fetch blob
            const fetchBlob = async (url: string) => {
                let fetchUrl = url;
                // Use proxy in development to bypass CORS
                if (import.meta.env.DEV && url.includes('firebasestorage.googleapis.com')) {
                    fetchUrl = url.replace('https://firebasestorage.googleapis.com', '/firebase-storage-proxy');
                }
                const response = await fetch(fetchUrl);
                return response.blob();
            };

            // Process Items
            // We need to resolve what selected items contain
            // If ID starts with 'year-', it's all docs in that year.
            // If ID starts with 'type-', it's all docs in that type.
            // If regular ID, it's a file.

            const itemsToProcess = items.filter(i => selectedItems.has(i.id));

            for (const item of itemsToProcess) {
                if (item.type === 'file') {
                    const doc = item.metadata as Document;
                    if (doc.fileUrl) {
                        try {
                            const blob = await fetchBlob(doc.fileUrl);
                            root?.file(`${item.name}.pdf`, blob); // Assuming PDF, could imply ext from url
                        } catch (e) {
                            console.error(`Failed to download ${item.name}`, e);
                        }
                    }
                } else {
                    // It's a folder (Year or Type)
                    // We need to find the underlying documents
                    const relativePath = (currentUser.role === 'agency' || currentUser.role === 'agency_member')
                        ? currentPath.slice(1)
                        : currentPath;

                    let docsInFolder: Document[] = [];

                    if (relativePath.length === 0) { // Year selected
                        const year = item.name;
                        docsInFolder = documents.filter(d => (d.ai_analysis?.doc_date || d.createdAt).includes(year));
                    } else if (relativePath.length === 1) { // Type selected
                        const currentYear = relativePath[0].name;
                        const type = item.name;
                        docsInFolder = documents.filter(d =>
                            (d.ai_analysis?.doc_date || d.createdAt).includes(currentYear) &&
                            d.typeName === type
                        );
                    }

                    // Add files to a subfolder in Zip
                    const subFolder = root?.folder(item.name);
                    for (const doc of docsInFolder) {
                        if (doc.fileUrl) {
                            try {
                                const blob = await fetchBlob(doc.fileUrl);
                                const fileName = doc.title || doc.typeName || doc.id;
                                subFolder?.file(`${fileName}.pdf`, blob);
                            } catch (e) {
                                console.error(`Failed to download ${doc.id}`, e);
                            }
                        }
                    }
                }
            }

            // Generate Zip
            const content = await zip.generateAsync({ type: "blob" });

            // Trigger Download
            const url = window.URL.createObjectURL(content);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${folderName}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

        } catch (error) {
            alert('Failed to generate zip file');
            console.error(error);
        } finally {
            setIsDownloading(false);
            setSelectedItems(new Set()); // Clear selection
        }
    };

    const handleBreadcrumbClick = (index: number) => {
        const newPath = currentPath.slice(0, index + 1);
        setCurrentPath(newPath);
    };

    // Helper to render Checkbox
    const SelectCheckbox = ({ id, isHovered }: { id: string, isHovered: boolean }) => {
        const isSelected = selectedItems.has(id);
        if (!isSelected && !isHovered && selectedItems.size === 0) return null; // Hidden by default

        return (
            <div
                onClick={(e) => handleToggleSelect(e, id)}
                className={`absolute top-2 left-2 z-10 p-1 rounded-md cursor-pointer transition-all ${isSelected ? 'bg-blue-500 text-white' : 'bg-white/80 text-slate-400 hover:text-black hover:bg-white inset-ring inset-ring-slate-200'}`}
            >
                {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
            </div>
        );
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col h-[calc(100vh-120px)] animate-fadeIn relative overflow-hidden">
            {/* Header / Toolbar */}
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-t-2xl">
                <div className="flex items-center gap-3 flex-1 overflow-hidden">
                    {/* Navigation Controls */}
                    <div className="flex items-center gap-1 text-slate-500">
                        <button
                            onClick={() => { setCurrentPath([]); setSearchQuery(''); }}
                            className={`p-1.5 rounded-lg hover:bg-slate-200 transition ${currentPath.length === 0 ? 'text-slate-400 cursor-default' : 'text-slate-700'}`}
                            disabled={currentPath.length === 0}
                        >
                            <Home size={18} />
                        </button>
                        <button
                            onClick={() => setCurrentPath(prev => prev.slice(0, prev.length - 1))}
                            className={`p-1.5 rounded-lg hover:bg-slate-200 transition ${currentPath.length === 0 ? 'text-slate-400 cursor-default' : 'text-slate-700'}`}
                            disabled={currentPath.length === 0}
                        >
                            <ArrowLeft size={18} />
                        </button>
                    </div>

                    {/* Breadcrumbs */}
                    <div className="flex items-center gap-1 text-sm text-slate-600 px-2 border-l border-slate-300 ml-1 overflow-hidden whitespace-nowrap mask-linear-fade">
                        <span className="cursor-pointer hover:underline hover:text-black font-medium" onClick={() => { setCurrentPath([]); setSearchQuery(''); }}>
                            Browse
                        </span>
                        {currentPath.map((item, idx) => (
                            <React.Fragment key={idx}>
                                <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />
                                <span
                                    className={`truncate max-w-[150px] ${idx === currentPath.length - 1 ? 'font-bold text-slate-900' : 'cursor-pointer hover:underline hover:text-black'}`}
                                    onClick={() => handleBreadcrumbClick(idx)}
                                >
                                    {item.name}
                                </span>
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* Right Actions */}
                <div className="flex items-center gap-3">
                    {/* Search */}
                    <div className="relative group">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-black transition-colors" />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 pr-3 py-1.5 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-black focus:border-black w-48 transition-all"
                        />
                    </div>

                    {/* View Toggle */}
                    <div className="flex items-center p-1 bg-slate-100 rounded-lg border border-slate-200">
                        <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-md transition ${viewMode === 'grid' ? 'bg-white shadow-sm text-black' : 'text-slate-500 hover:text-black'}`}><Grid size={16} /></button>
                        <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition ${viewMode === 'list' ? 'bg-white shadow-sm text-black' : 'text-slate-500 hover:text-black'}`}><ListIcon size={16} /></button>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6 bg-slate-50/30">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                        <Loader2 size={32} className="animate-spin mb-2" />
                        <p className="text-sm">Loading contents...</p>
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                        <Folder size={48} className="mb-4 opacity-20" />
                        <p>This folder is empty.</p>
                        {searchQuery && <p className="text-xs mt-2">No results found for "{searchQuery}"</p>}
                    </div>
                ) : (
                    <div className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4' : 'flex flex-col gap-1'}>
                        {items.map(item => (
                            <div
                                key={item.id}
                                onClick={(e) => handleItemClick(item, e)}
                                className={`
                                    group relative cursor-pointer border hover:border-blue-200 hover:bg-blue-50/50 transition-all select-none
                                    ${viewMode === 'grid'
                                        ? 'flex flex-col items-center p-4 rounded-xl text-center'
                                        : 'flex items-center px-4 py-3 rounded-lg bg-white mb-1 hover:shadow-sm'
                                    }
                                    ${selectedItems.has(item.id) ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-transparent border-slate-100'}
                                `}
                            >
                                {/* Selection Checkbox */}
                                {viewMode === 'grid' && (
                                    <div
                                        onClick={(e) => handleToggleSelect(e, item.id)}
                                        className={`absolute top-2 left-2 z-10 p-1 rounded-md cursor-pointer transition-all ${selectedItems.has(item.id) ? 'bg-blue-500 text-white' : 'bg-white/80 text-slate-400 opacity-0 group-hover:opacity-100 hover:text-black hover:bg-white shadow-sm'}`}
                                    >
                                        {selectedItems.has(item.id) ? <CheckSquare size={16} /> : <Square size={16} />}
                                    </div>
                                )}

                                {viewMode === 'list' && (
                                    <div onClick={(e) => handleToggleSelect(e, item.id)} className={`mr-3 ${selectedItems.has(item.id) ? 'text-blue-500' : 'text-slate-300 hover:text-slate-500'}`}>
                                        {selectedItems.has(item.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                                    </div>
                                )}

                                {/* Icon */}
                                <div className={`
                                    flex items-center justify-center shrink-0
                                    ${viewMode === 'grid'
                                        ? 'w-16 h-16 mb-3 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 shadow-sm group-hover:shadow-md transition-all group-hover:scale-105'
                                        : 'w-10 h-10 mr-4 rounded-lg bg-slate-50'
                                    }
                                `}>
                                    {item.type === 'folder' ? (
                                        <Folder size={viewMode === 'grid' ? 32 : 20} className="text-blue-400 fill-blue-400/20" strokeWidth={1.5} />
                                    ) : (
                                        <FileText size={viewMode === 'grid' ? 32 : 20} className="text-slate-400 group-hover:text-blue-500 transition-colors" strokeWidth={1.5} />
                                    )}
                                </div>

                                {/* Text Info */}
                                <div className={viewMode === 'grid' ? 'w-full' : 'flex-1 flex items-center justify-between'}>
                                    <div className="overflow-hidden">
                                        <h3 className={`font-medium text-slate-700 group-hover:text-blue-700 truncate ${viewMode === 'grid' ? 'text-sm' : 'text-sm'}`}>
                                            {item.name}
                                        </h3>
                                        {item.type === 'file' && viewMode === 'list' && (
                                            <p className="text-xs text-slate-400 mt-0.5">
                                                {formatDate(item.metadata.createdAt, currentUser.dateFormat)} • {(item.metadata.size / 1024).toFixed(0)} KB
                                            </p>
                                        )}
                                        {item.type === 'folder' && viewMode === 'list' && (
                                            <p className="text-xs text-slate-400 mt-0.5">Folder</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Action Bar (Floating) */}
            {selectedItems.size > 0 && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-6 animate-slideUp z-50">
                    <span className="font-medium text-sm">{selectedItems.size} selected</span>
                    <div className="h-4 w-px bg-slate-700"></div>
                    <button
                        onClick={handleDownloadSelected}
                        disabled={isDownloading}
                        className="flex items-center gap-2 text-sm font-medium hover:text-blue-400 transition-colors disabled:opacity-50"
                    >
                        {isDownloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        {isDownloading ? 'Zipping...' : 'Download ZIP'}
                    </button>
                    <button onClick={() => setSelectedItems(new Set())} className="text-slate-500 hover:text-white transition-colors">
                        <X size={18} />
                    </button>
                </div>
            )}

            {/* Footer */}
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-[10px] text-slate-400 flex justify-between rounded-b-2xl">
                <span>{items.length} items</span>
                <span>{currentPath.length > 0 ? `/${currentPath.map(p => p.name).join('/')}` : '/'}</span>
            </div>
        </div>
    );
}
